import { createSelector } from "reselect"
import { get, map, find, chain, filter, includes } from "lodash"
import pluralize from "pluralize"
import { computeAvailableSelectViewFactors, getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData } from "helpers/factors"
import { NORMALIZATION } from "helpers/segments"
import { createQueryStringSelector } from "selectors/queryString"
import { getCurrent, getMCTEAdjustedSegment, getRiskRaw } from "./risk"
import { getDisabled } from "."
import { ATTRIBUTION_COLORS, ATTRIBUTION_AREAS } from "helpers/analyzeConstants"

const OVERALL_ITEMS = [{
  id: "total",
  color: ATTRIBUTION_COLORS.TOTAL,
  label: "Total",
  field: "total",
  type: "overall",
  scaleY: "left",
  scaleColor: true,
}, {
  id: "factor",
  color: ATTRIBUTION_COLORS.FACTOR,
  area: ATTRIBUTION_AREAS.FACTOR,
  label: "Factor",
  field: "factorsTotal",
  type: "overall",
  scaleY: "right",
}, {
  id: "specific",
  color: ATTRIBUTION_COLORS.ALPHA,
  area: ATTRIBUTION_AREAS.ALPHA,
  label: "Specific",
  field: "specific",
  type: "overall",
  scaleY: "right",
},
{
  id: "segment",
  color: ATTRIBUTION_COLORS.SEGMENT,
  label: "Segment",
  field: "total",
  area: ATTRIBUTION_AREAS.TRANSPARENT,
  fn: (datum) => chain(datum)
    .pick(["specific", "factorsTotal"])
    .map()
    .sum()
    .value(),
  type: "overall",
  scaleY: "right",
  scaleColor: true,
}]

export const getScaleIds = () => chain(OVERALL_ITEMS)
  .groupBy("scaleY")
  .mapValues(item => map(item, ({ id }) => id))
  .value()

export function isSegmentFilter(item) { return get(item, "id") === "segment" }
export const getCategories = (_, props) => props.categories
export const getFavoriteFactors = (_, props) => props.favoriteFactors
const getRawFactorCategoryId = createQueryStringSelector("factorCategory", "total")
const getSelectedViews = (_, props) => props.selectedViews
const getFactorsMetadata = (_, props) => props.factorsMetadata
const getFactorsClassification = (_, props) => props.factorsClassification

export const getFactorCategoryId = createSelector(
  [getRawFactorCategoryId, getCategories],
  (factorCategoryId, categories) => {
    if (factorCategoryId === "total" || factorCategoryId === "factors") {
      return factorCategoryId
    } else if (find(categories, cat => cat.id === factorCategoryId)) {
      return factorCategoryId
    }

    return "total"
  },
)

export const getComputedFactors = createSelector(
  [getFactorsMetadata, getCurrent, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, disabled, favoriteFactors) => chain(lastDatum)
    .get("factors")
    .map((risk, id) => ({
      id,
      lastValue: risk,
      label: getFactorLabel(factorsMetadata, id),
      category: getFactorCategory(factorsMetadata, id),
      isAggregated: getIsAggregatedFactor(factorsMetadata, id),
      favorite: favoriteFactors[id],
      field: `factors.${id}`,
      disabled: includes(disabled, id),
      type: "factors",
    }))
    .value(),
)

const getNonZerosKeys = makeNonZeroKeysSelectorFromData({
  getter: (value) => get(value, "attribution.factors"),
  isAboveZeroTreshold: (value) => value !== 0,
  dataSelector: getRiskRaw,
  favoriteSelector: getFavoriteFactors,
})

export const getAvailableSelectViewFactors = createSelector(
  [getComputedFactors, getFactorsClassification, getSelectedViews, getNonZerosKeys],
  computeAvailableSelectViewFactors,
)

export const getShowSegment = createSelector(
  [getMCTEAdjustedSegment],
  (segment) => Boolean(segment && segment.normalization === NORMALIZATION.CONTRIBUTION),
)

export const getOverallItems = createSelector(
  [getCategories, getDisabled, getShowSegment],
  (categories, disabled, showSegment) => {
    const items = showSegment ? OVERALL_ITEMS : filter(OVERALL_ITEMS, (item) => !isSegmentFilter(item))
    return [
      ...map(items, item => ({ ...item, disabled: includes(disabled, item.id) })),
      {
        id: "factor_total",
        color: ATTRIBUTION_COLORS.FACTOR,
        label: "All Factors",
        categoryLabel: "Factors",
        field: "factorsTotal",
        disabled: includes(disabled, "factor_total"),
        type: "category",
      },
      ...chain(categories)
        .filter(category => !category.isSelectedView)
        .map(category => ({
          id: category.id,
          color: category.color,
          label: category.label,
          field: `categories.${category.id}`,
          disabled: includes(disabled, category.id),
          type: "category",
        })),
      ...map(categories, category => ({
        id: `${category.id}_total`,
        color: category.color,
        categoryLabel: category.isSelectedView ? category.label : pluralize(category.label),
        category: category.id,
        label: category.isSelectedView ? category.label : `All ${category.label} Factors`,
        field: `categories.${category.id}`,
        disabled: includes(disabled, `${category.id}_total`),
      })),
    ]
  },
)

export const getAllItems = createSelector(
  [getComputedFactors, getOverallItems],
  (factors, overallItems) => ([
    ...overallItems,
    ...factors,
  ]),
)

export const getAllCategoryItems = createSelector(
  [getComputedFactors, getOverallItems, getFactorCategoryId],
  (factors, overallItems, factorCategoryId) => {
    switch (factorCategoryId) {
      case "total":
        return filter(overallItems, { type: "overall" })
      case "factors":
        return chain(overallItems)
          .filter({ type: "category" })
          .value()
      default: {
        const overall = chain(overallItems)
          .filter({ category: factorCategoryId })
          .value()

        return [
          ...overall,
          ...chain(factors)
            .filter({ category: factorCategoryId })
            .orderBy([item => Math.abs(item.lastValue)], ["desc"])
            .value(),
        ]
      }
    }
  },
)

export const getOrderedCategoryItems = createSelector(
  [getAllCategoryItems, getFactorCategoryId],
  (items, factorCategoryId) => {
    if (factorCategoryId !== "total" && factorCategoryId !== "factors") {
      const overall = items[0]
      const factors = items.slice(1)
      return [
        overall,
        ...chain(factors)
          .orderBy([item => item.favorite, item => Math.abs(item.lastValue)], ["asc", "desc"])
          .value(),
      ]
    }

    return items
  },
)
